import React, { useEffect, useReducer, useState } from "react";
import DashboardContainer from "../../components/dashboard-container";
import {
  Box,
  Button,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { useTheme } from "@mui/styles";
import { asnService } from "../../utils/services/asnService";
import PurchaseOrderList from "../../components/purchase-order-list";
import { useLocation, useNavigate } from "react-router-dom";
import buildQuery from "../../utils/query-builder";
import { POFilterInitialState } from "../../utils/filter-config";
import CustomTypography from "../../globalStyles/fonts";
import ImportIcon from "../../globalStyles/icons/importIcon";
import { isMobile } from "../../globalStyles/mobile";
import SyncIcon from "@mui/icons-material/Sync";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import { toast } from "react-toastify";
import { pipe17Service } from "../../utils/services/pipe17Service";

function filterReducer(state, action) {
  switch (action.type) {
    case "SET_FILTER":
      return {
        ...state,
        columns: state?.columns.map((x) =>
          x?.key === action?.payload?.key ? { ...x, ...action.payload } : x,
        ),
      };
    case "SET_OPTIONS":
      return {
        ...state,
        columns: state.columns.map((col) =>
          col.key === action.payload.key
            ? { ...col, options: action.payload.options }
            : col,
        ),
      };
    case "UPDATE_LIMIT":
      return { ...state, limit: action.payload };
    case "EXISTING_DATA":
      return {
        ...state,
        columns: state.columns.map((x) => ({
          ...x,
          filter:
            action.data.filters.find((y) => y.key === x.key)?.values ||
            x.filter,
        })),
        limit: action.data.limit || state.limit,
      };

    default:
      return state;
  }
}
const PurchaseOrder = () => {
  const [filterData, dispatch] = useReducer(
    filterReducer,
    POFilterInitialState,
  );
  const [selectedAsn, setSelectedAsn] = useState(null);
  const [asnList, setAsnList] = useState([]);
  const [viewMode, setViewMode] = useState("list");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(15);
  const [dataCount, setDataCount] = useState(rowsPerPage);
  const [loading, setLoading] = useState(false);
  // Pipe17 PO backfill ("Sync POs"). `pipe17Ready` gates the button on the
  // client actually being onboarded on Pipe17 — POs are pulled from the
  // provider, so the action is meaningless for a client without one.
  const [pipe17Ready, setPipe17Ready] = useState(false);
  const [syncingPOs, setSyncingPOs] = useState(false);
  const clientId = localStorage.getItem("client_id");
  const navigate = useNavigate();
  const location = useLocation();
  const roles = JSON.parse(localStorage.getItem("roles")) || [];
  const theme = useTheme();
  const primary_color = theme.palette?.primary?.main || "#D13438";

  const updateFilterData = () => {
    localStorage.setItem(
      "po-filter-data",
      JSON.stringify({
        populate: filterData.populate,
        filters: filterData.columns.map((x) => ({
          key: x.key,
          values: x.filter,
        })),
        limit: filterData.limit,
      }),
    );
  };

  const fetchAsn = async () => {
    setLoading(true);
    try {
      // Inventory purchase orders only
      const response = await asnService.fetch(
        buildQuery({ ...filterData, limit: rowsPerPage }, page),
        clientId,
        "Regular",
      );
      setAsnList(response.data);
      setLoading(false);
      setDataCount(response.total);
    } catch (error) {
      console.error("Fetch failed:", error);
      setLoading(false);
    }
  };
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const response = await asnService.fetch(
          buildQuery({ ...filterData, limit: rowsPerPage }, page),
          clientId,
          "Regular",
        );
        if (!cancelled) {
          setAsnList(response.data);
          setLoading(false);
          setDataCount(response.total);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Fetch failed:", error);
          setLoading(false);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [page, rowsPerPage, filterData]);

  const handleFilterChange = (newFilter) => {
    dispatch({ type: "SET_FILTER", payload: newFilter });
    setPage(0);
  };

  // Is this client onboarded on Pipe17? Only then can we pull POs from it.
  // A failure here is not worth surfacing — it just leaves the button hidden.
  useEffect(() => {
    let cancelled = false;
    if (!clientId) return undefined;
    pipe17Service
      .status(clientId)
      .then((res) => {
        if (!cancelled) setPipe17Ready(Boolean(res?.oms_config));
      })
      .catch(() => {
        if (!cancelled) setPipe17Ready(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // Pull the client's Pipe17 purchase-order history. The backend answers as soon
  // as the paging job is QUEUED — the POs arrive asynchronously as their inbound
  // references drain — so this cannot refetch and show the result immediately.
  // Say so plainly rather than refreshing into an unchanged list.
  const handleSyncPurchaseOrders = async () => {
    setSyncingPOs(true);
    try {
      await pipe17Service.syncPurchaseOrders(clientId);
      toast.success(
        "Purchase-order sync started — POs will appear over the next few minutes. Use Refresh to check.",
      );
    } catch (e) {
      toast.error(
        e?.response?.data?.message || "Failed to start the purchase-order sync",
      );
    } finally {
      setSyncingPOs(false);
    }
  };

  useEffect(() => {
    let data = localStorage.getItem("po-filter-data");
    if (location.search.includes("child") && data) {
      localStorage.removeItem("po-filter-data");
      dispatch({
        type: "EXISTING_DATA",
        data: JSON.parse(data),
      });
    }
  }, []);

  return (
    <>
      <DashboardContainer
        filterButton={false}
        hideUploadButton
        header={"Purchase Orders"}
        title={"+ Add"}
        onClickAddButton={() => {
          updateFilterData();
          navigate("/add-asn");
        }}
        setViewMode={setViewMode}
        onClickRefresh={() => fetchAsn()}
        hideFilterClient
        showRefreshButton
        headerButtons={
          clientId ? (
            <Box display="flex" alignItems="center" gap={1}>
              {pipe17Ready ? (
                <Tooltip title="Pull this client's purchase-order history from Pipe17">
                  <span>
                    {isMobile() ? (
                      <IconButton
                        aria-label="Sync purchase orders from Pipe17"
                        disabled={syncingPOs}
                        onClick={handleSyncPurchaseOrders}
                      >
                        {syncingPOs ? (
                          <CircularProgress size={20} />
                        ) : (
                          <SyncIcon sx={{ color: primary_color }} />
                        )}
                      </IconButton>
                    ) : (
                      <Button
                        size="small"
                        style={{ textTransform: "none" }}
                        variant="outlined"
                        disabled={syncingPOs}
                        onClick={handleSyncPurchaseOrders}
                        startIcon={
                          syncingPOs ? (
                            <CircularProgress size={16} />
                          ) : (
                            <SyncIcon fontSize="small" />
                          )
                        }
                      >
                        <CustomTypography fontSize="14px" fontWeight={500}>
                          {syncingPOs ? "Starting…" : "Sync POs"}
                        </CustomTypography>
                      </Button>
                    )}
                  </span>
                </Tooltip>
              ) : null}
              {isMobile() ? (
                <IconButton
                  onClick={() => {
                    updateFilterData();
                    navigate("/purchase-order-import");
                  }}
                >
                  <ImportIcon fill={primary_color} />
                </IconButton>
              ) : (
                <Button
                  size="small"
                  style={{ textTransform: "none" }}
                  variant="contained"
                  onClick={() => {
                    updateFilterData();
                    navigate("/purchase-order-import");
                  }}
                >
                  <ImportIcon fill="#FFF" />
                  <CustomTypography fontSize="14px" fontWeight={500} color="#FFFFFF">
                    Import
                  </CustomTypography>
                </Button>
              )}
            </Box>
          ) : null
        }
      >
        {/* <Card elevation={0} sx={{ borderRadius: 0, maxWidth: "100%" }}> */}
        {/* <CardContent> */}
        {loading ? (
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell>
                    <Skeleton variant="text" width="80%" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width="80%" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width="80%" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width="80%" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width="80%" />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from({ length: 15 }).map((_, rowIndex) => (
                  <TableRow key={rowIndex}>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <PurchaseOrderList
            orderType="Inventory"
            dataCount={dataCount}
            page={page}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            setPage={setPage}
            onDeleteData={fetchAsn}
            onEditData={(e) => setSelectedAsn(e)}
            data={asnList}
            viewMode={viewMode}
            selectedAsn={selectedAsn}
            setSelectedAsn={setSelectedAsn}
            onChangeFilter={handleFilterChange}
            filterData={filterData}
            roles={roles}
          />
        )}
        {/* </CardContent> */}
        {/* </Card> */}
      </DashboardContainer>
    </>
  );
};

export default PurchaseOrder;
